import { useMemo, useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import { useAppContext } from '@/context/context';
import { HeatmapContact, HeatmapMode } from './useHeatmapData';
import { HeatmapCenter } from './useCentersData';
import { BranchPolygonData } from './useBranchPolygon';
import { ContactMarkersComponent } from './ContactMarkersComponent';
import { CenterMarkersComponent } from './CenterMarkersComponent';
import { PolygonWithBounds } from '@/components/dashboard/Map/MyMap';
import { calculateBoundsWithCenters } from './heatmapUtils';

const center = { lat: 31.794431983241022, lng: 35.18878195954886 };
const MAP_PADDING = { top: 50, right: 50, bottom: 50, left: 50 };

interface HeatmapViewProps {
  contacts: HeatmapContact[];
  centers: HeatmapCenter[];
  mode: HeatmapMode;
  branchPolygon: BranchPolygonData | null;
}

export const HeatmapView = ({ contacts, centers, mode, branchPolygon }: HeatmapViewProps) => {
  const { isGoogleMapsApiLoaded, googleMapsApiLoadError } = useAppContext();
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const containerStyle = { height: '100%', width: '100%' };

  // Only calculate bounds for contacts/centers if there's no polygon
  // If polygon exists, PolygonWithBounds will handle the bounds automatically
  const bounds = useMemo(
    () => (branchPolygon ? null : calculateBoundsWithCenters(contacts, centers)),
    [contacts, centers, branchPolygon]
  );

  useEffect(() => {
    if (mapInstance && bounds) {
      mapInstance.fitBounds(bounds, MAP_PADDING);
    }
  }, [mapInstance, bounds]);

  const mapOptions = useMemo(
    () => ({
      fullscreenControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      mapTypeControlOptions: { position: google.maps.ControlPosition.BOTTOM_LEFT },
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER }
    }),
    []
  );

  if (googleMapsApiLoadError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        קרתה שגיאה בעת טעינת המפה
      </Box>
    );
  }

  if (!isGoogleMapsApiLoaded) {
    return <Skeleton sx={{ width: '100%', height: '100%' }} variant="rectangular" />;
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={bounds ? bounds.getCenter().toJSON() : center}
        zoom={bounds ? undefined : 10}
        options={mapOptions}
        onLoad={map => {
          setMapInstance(map);
          if (bounds) {
            map.fitBounds(bounds, MAP_PADDING);
          }
        }}
      >
        {branchPolygon && (
          <PolygonWithBounds
            polygon={{ geom: { coordinates: [[branchPolygon.coordinates[0] || branchPolygon.coordinates]] } }}
          />
        )}
        <ContactMarkersComponent contacts={contacts} />
        <CenterMarkersComponent centers={centers} />
      </GoogleMap>
    </Box>
  );
};
